import { Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { NgxMatDatetimePicker } from '@angular-material-components/datetime-picker';
import { Subscription } from 'rxjs';

const AUTO_CONFIRM_PANEL_CLASS = 'ngx-picker-auto-confirm';

@Directive({
  selector: 'ngx-mat-datetime-picker[appAutoConfirm]'
})
export class DatePickerAutoConfirmDirective<D> implements OnInit, OnChanges, OnDestroy {

  @Input() appAutoConfirm = false;
  @Input() hideTime = false;

  private originalSelect: (date: D) => void;
  private openedSub: Subscription | null = null;
  private monthViewSubs: Subscription[] = [];

  constructor(private picker: NgxMatDatetimePicker<D>) {}

  ngOnInit(): void {
    this.originalSelect = this.picker.select.bind(this.picker);
    this.picker.select = (date: D) => {
      this.originalSelect(date);
      if (this.appAutoConfirm && this.picker._hideTime) {
        this.picker.ok();
      }
    };
    // Kalendarz woła datepicker.select() tylko przy ZMIANIE daty - kliknięcie już
    // zaznaczonego dnia (w tym "dziś" przy pustym polu, bo picker startuje od dziś)
    // nie wybiera nic. Widok miesiąca emituje jednak _userSelection z datą przy każdym
    // kliknięciu - przechwytujemy je i przy braku selectedChange wołamy select() sami.
    this.openedSub = this.picker.openedStream.subscribe(() => setTimeout(() => this.hookMonthView()));
    this.updatePanelClass();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.appAutoConfirm || changes.hideTime) && !changes.appAutoConfirm?.firstChange) {
      this.updatePanelClass();
    }
  }

  ngOnDestroy(): void {
    this.picker.select = this.originalSelect;
    this.openedSub?.unsubscribe();
    this.clearMonthViewSubs();
  }

  private hookMonthView(): void {
    this.clearMonthViewSubs();
    if (!this.appAutoConfirm || !this.picker._hideTime) return;
    const content: any = (this.picker as any)._popupComponentRef?.instance
      ?? (this.picker as any)._dialogRef?.componentInstance;
    const calendar: any = content?._calendar;
    const monthView: any = calendar?.monthView;
    if (!calendar || !monthView) return;

    // selectedChange (tylko inna data) emituje się synchronicznie przed _userSelection
    // (każde kliknięcie) - flaga rozróżnia "normalny" wybór od kliknięcia tej samej daty
    let dateChanged = false;
    this.monthViewSubs.push(
      monthView.selectedChange.subscribe(() => dateChanged = true),
      monthView._userSelection.subscribe((event: any) => {
        if (!dateChanged && event?.value != null) {
          this.picker.select(event.value);
        }
        dateChanged = false;
      }),
      this.picker.closedStream.subscribe(() => this.clearMonthViewSubs())
    );

    // Po powrocie z widoku roku/lat widok miesiąca powstaje od nowa - przepinamy się
    if (!calendar.__autoConfirmGotoPatched) {
      calendar.__autoConfirmGotoPatched = true;
      const originalGoto = calendar._goToDateInView.bind(calendar);
      calendar._goToDateInView = (date: unknown, view: string) => {
        originalGoto(date, view);
        if (view === 'month') {
          setTimeout(() => this.hookMonthView());
        }
      };
    }
  }

  private clearMonthViewSubs(): void {
    this.monthViewSubs.forEach(s => s.unsubscribe());
    this.monthViewSubs = [];
  }

  private updatePanelClass(): void {
    let classes: string[] = [];
    if (Array.isArray(this.picker.panelClass)) {
      classes = this.picker.panelClass.filter(c => c !== AUTO_CONFIRM_PANEL_CLASS);
    } else if (this.picker.panelClass) {
      classes = [this.picker.panelClass];
    }
    if (this.appAutoConfirm && this.hideTime) {
      classes.push(AUTO_CONFIRM_PANEL_CLASS);
    }
    this.picker.panelClass = classes;
  }
}
